/**
 * DOT intersection shapefiles for the Striping Plan map tab (spec 2026-10-14 Part B):
 * the user picks a .zip; it is read in the browser (shpjs reprojects to WGS84 with the
 * .prj) and never uploaded. Points are kept when within 150 ft of a lane of the selected
 * route and not within 50 ft of a point already in its list.
 */
import JSZip from "jszip";
import { distanceFt } from "./stripingGeometry";
import { insideBounds, isNearAnyLine, linesBounds } from "./stripingIntersections";
import {
  MAX_STRIPING_NAME, MAX_STRIPING_POINTS, STRIPING_MATCH_FT, STRIPING_MERGE_FT,
} from "./stripingList";

export const DOT_NAME_COLUMNS = [
  "NAME", "ROAD_NAME", "STREET", "FULLNAME", "RD_NAME", "SecondaryR", "IntName",
];
export const NO_PRJ_MESSAGE = "the file has no .prj, so its coordinates can't be placed";

export class DotImportError extends Error {}

const nameOf = (properties) => {
  const byLower = new Map(
    Object.entries(properties || {}).map(([k, v]) => [k.toLowerCase(), v]),
  );
  for (const column of DOT_NAME_COLUMNS) {
    const value = byLower.get(column.toLowerCase());
    const text = value == null ? "" : String(value).trim();
    if (text) return text.slice(0, MAX_STRIPING_NAME);
  }
  return "";
};

/** A shape's first coordinate: the point itself, or a line's start — the striping
 * service's DOT reader takes shape.points[0] too. */
const firstCoordinate = (geometry) => {
  const c = geometry?.coordinates;
  switch (geometry?.type) {
    case "Point": return c;
    case "MultiPoint":
    case "LineString": return c?.[0];
    case "MultiLineString": return c?.[0]?.[0];
    default: return null;
  }
};

/** [{lat, lng, name}] from the shapefile's GeoJSON layers. */
export const featuresToCandidates = (collections) => {
  const out = [];
  for (const collection of collections || []) {
    for (const f of collection?.features || []) {
      const coord = firstCoordinate(f?.geometry);
      const lng = coord == null || coord[0] == null ? NaN : Number(coord[0]);
      const lat = coord == null || coord[1] == null ? NaN : Number(coord[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
      out.push({ lat, lng, name: nameOf(f?.properties) });
    }
  }
  return out;
};

/** How many features the layers hold, usable or not. */
export const featureCount = (collections) =>
  (collections || []).reduce((n, c) => n + (c?.features?.length || 0), 0);

export const planDotImport = ({
  candidates, lines, existing = [], unusable = 0,
  matchFt = STRIPING_MATCH_FT, mergeFt = STRIPING_MERGE_FT, maxPoints = MAX_STRIPING_POINTS,
}) => {
  const added = [];
  let offRoute = 0;
  let duplicates = 0;
  let overLimit = 0;
  const bounds = linesBounds(lines, matchFt);
  for (const c of candidates || []) {
    if (!insideBounds(c, bounds) || !isNearAnyLine(c, lines, matchFt)) { offRoute += 1; continue; }
    if ([...existing, ...added].some((p) => distanceFt(p, c) < mergeFt)) { duplicates += 1; continue; }
    if (existing.length + added.length >= maxPoints) { overLimit += 1; continue; }
    added.push(c);
  }
  return { added, total: (candidates || []).length, offRoute, duplicates, overLimit, unusable };
};

export const importNoteText = ({ added, total, offRoute, duplicates, overLimit, unusable = 0 }) =>
  `Added ${added.length} of ${total} (${offRoute} not on this route, ` +
  `${duplicates} duplicate${duplicates === 1 ? "" : "s"}` +
  `${overLimit ? `, ${overLimit} over the ${MAX_STRIPING_POINTS}-point limit` : ""})` +
  (unusable > 0
    ? `; ${unusable} feature${unusable === 1 ? "" : "s"} had no usable point (polygons or unprojected coordinates)`
    : "");

const readBuffer = (file) =>
  typeof file?.arrayBuffer === "function"
    ? file.arrayBuffer()
    : new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });

/** The zip's layers as GeoJSON FeatureCollections in WGS84. Throws DotImportError. */
export const readDotZip = async (file) => {
  const buffer = await readBuffer(file);
  let zip;
  try {
    zip = await JSZip.loadAsync(buffer);
  } catch (e) {
    throw new DotImportError("the file is not a .zip of a shapefile");
  }
  const names = Object.keys(zip.files).filter((n) => !zip.files[n].dir);
  const stems = (ext) => new Set(
    names.filter((n) => n.toLowerCase().endsWith(ext)).map((n) => n.slice(0, -ext.length).toLowerCase()),
  );
  const shp = stems(".shp");
  if (!shp.size) throw new DotImportError("the .zip has no .shp file");
  const prj = stems(".prj");
  if ([...shp].some((stem) => !prj.has(stem))) throw new DotImportError(NO_PRJ_MESSAGE);
  let parsed;
  try {
    // Loaded on demand: the parser (and its unzip/proj4 code) is only fetched when a
    // user imports a file.
    const { default: parseShapefile } = await import("shpjs");
    parsed = await parseShapefile(buffer);
  } catch (e) {
    throw new DotImportError(`the shapefile could not be read (${e?.message || e})`);
  }
  return Array.isArray(parsed) ? parsed : [parsed];
};
